import React, { useCallback, useRef, useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { cn } from '@/utils/cn';

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

type FixedPosition = { left: number; top: number };

const GAP = 8;

export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [fixedPosition, setFixedPosition] = useState<FixedPosition | null>(null);
  const triggerRef = useRef<View>(null);

  const sideClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full right-0 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  /**
   * Na web o gatilho costuma morar dentro de um container com overflow (a
   * navegação da sidebar é um ScrollView): um balão `absolute` seria cortado
   * na borda. Medimos o gatilho e posicionamos em `fixed`, fora do recorte.
   */
  const show = useCallback(() => {
    if (Platform.OS === 'web') {
      const node = triggerRef.current as unknown as HTMLElement | null;
      const rect = node?.getBoundingClientRect?.();
      if (rect) {
        const position: FixedPosition =
          side === 'right'
            ? { left: rect.right + GAP, top: rect.top + rect.height / 2 }
            : side === 'left'
              ? { left: rect.left - GAP, top: rect.top + rect.height / 2 }
              : side === 'bottom'
                ? { left: rect.left + rect.width / 2, top: rect.bottom + GAP }
                : { left: rect.left + rect.width / 2, top: rect.top - GAP };
        setFixedPosition(position);
      }
    }
    setIsVisible(true);
  }, [side]);

  const hide = useCallback(() => setIsVisible(false), []);

  const transform =
    side === 'right'
      ? 'translateY(-50%)'
      : side === 'left'
        ? 'translate(-100%, -50%)'
        : side === 'bottom'
          ? 'translateX(-50%)'
          : 'translate(-50%, -100%)';

  return (
    <View className="relative" ref={triggerRef} collapsable={false}>
      <Pressable
        onPressIn={show}
        onPressOut={hide}
        // @ts-ignore - onHoverIn/onHoverOut are web-only props
        onHoverIn={show}
        // @ts-ignore - onHoverIn/onHoverOut are web-only props
        onHoverOut={hide}
      >
        {children}
      </Pressable>
      {isVisible && (
        <View
          pointerEvents="none"
          className={cn(
            'z-50 max-w-[300px] rounded-md border bg-ink px-3 py-1.5 text-xs text-white shadow-md',
            fixedPosition ? '' : `absolute ${sideClasses[side]}`,
            className,
          )}
          style={
            fixedPosition
              ? ({
                  position: 'fixed',
                  left: fixedPosition.left,
                  top: fixedPosition.top,
                  transform,
                } as never)
              : undefined
          }
        >
          {typeof content === 'string' ? (
            <Text className="text-xs text-white">{content}</Text>
          ) : (
            content
          )}
        </View>
      )}
    </View>
  );
}
